import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users, Shield, Power } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VOLUNTEER_ROLES, VOLUNTEER_ROLE_LABELS } from "@/lib/roles";

const selectCls = "rounded-lg border border-[#e4d8c4] bg-white px-2 py-1.5 text-xs font-semibold text-[#00334a]";
const PERMS = [
  { key: "manage_campaigns", label: "Campaigns" },
  { key: "manage_donations", label: "Donations" },
  { key: "manage_sponsors", label: "Sponsors" },
];

export default function AccountsManage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [accounts, setAccounts] = useState([]);

  const load = useCallback(() => { api.get("/admin/accounts").then(({ data }) => setAccounts(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const setActive = async (id, is_active) => {
    try { await api.post(`/admin/accounts/${id}/active`, { is_active }); toast.success(is_active ? "Reactivated" : "Deactivated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const promote = async (id, volunteer_role) => {
    try { await api.post(`/admin/accounts/${id}/promote`, { volunteer_role: volunteer_role || null }); toast.success("Role updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const setPerm = async (acc, key, val) => {
    const permissions = { ...(acc.permissions || {}), [key]: val };
    try { await api.put(`/admin/accounts/${acc.id}/permissions`, permissions); toast.success("Permissions updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="volunteer-accounts">
      <div>
        <span className="hf-eyebrow">People</span>
        <h1 className="mt-1 font-heading text-3xl font-semibold text-[#00334a]">Accounts</h1>
        <p className="mt-1 text-sm text-[#7c7a6a]">Supporters and volunteers. Deactivate blocks login without deleting history.</p>
      </div>

      {accounts.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#e4d8c4] bg-white p-12 text-center">
          <Users className="mx-auto h-10 w-10 text-[#e4d8c4]" />
          <p className="mt-3 font-semibold text-[#00334a]">No supporter or volunteer accounts yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {accounts.map((a) => (
            <div key={a.id} className="rounded-2xl border border-[#e4d8c4] bg-white p-4" data-testid={`account-${a.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-heading text-lg font-semibold text-[#00334a]">{a.name}</span>
                    <span className="rounded-full bg-[#efe9dd] px-2 py-0.5 text-[10px] font-bold uppercase text-[#5a5849]">{a.role}</span>
                    {a.volunteer_role && <span className="rounded-full bg-[#efe6d6] px-2 py-0.5 text-[10px] font-bold text-[#3f6670]">{a.volunteer_role_label}</span>}
                    {a.is_active === false && <span className="rounded-full bg-[#f3e7db] px-2 py-0.5 text-[10px] font-bold uppercase text-[#b05e3c]">Inactive</span>}
                  </div>
                  <div className="text-xs text-[#7c7a6a]">{a.email}{a.team_name ? ` · ${a.team_name}` : ""} · joined {new Date(a.created_at).toLocaleDateString()}</div>
                </div>
                <select value={a.volunteer_role || ""} onChange={(e) => promote(a.id, e.target.value)} className={selectCls} data-testid={`account-role-${a.id}`}>
                  <option value="">{a.role === "volunteer" ? "— (volunteer)" : "Not a volunteer"}</option>
                  {VOLUNTEER_ROLES.map((r) => <option key={r} value={r}>{VOLUNTEER_ROLE_LABELS[r]}</option>)}
                </select>
                <button
                  onClick={() => setActive(a.id, !(a.is_active !== false))}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${a.is_active !== false ? "bg-[#f3e7db] text-[#b05e3c]" : "bg-[#e8e7d6] text-[#5f6a48]"}`}
                  data-testid={`account-toggle-active-${a.id}`}
                >
                  <Power className="h-3.5 w-3.5" /> {a.is_active !== false ? "Deactivate" : "Reactivate"}
                </button>
              </div>

              {isAdmin && a.volunteer_role === "fundraising_admin" && (
                <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl bg-[#f8f3e9] p-3" data-testid={`account-perms-${a.id}`}>
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#00334a]"><Shield className="h-3.5 w-3.5 text-[#c97049]" /> Fundraising powers</span>
                  {PERMS.map((p) => (
                    <label key={p.key} className="flex items-center gap-1.5 text-sm font-semibold text-[#3f6670]">
                      <input type="checkbox" checked={!!(a.permissions || {})[p.key]} onChange={(e) => setPerm(a, p.key, e.target.checked)} className="h-4 w-4 accent-[#c97049]" data-testid={`perm-${p.key}-${a.id}`} />
                      {p.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
